import { Do } from "@rsm-hcd/javascript-core";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { notebookPinsLoadingAtom } from "utilities/atoms/notebooks-atom";
import PinnedPublicationsAtoms from "utilities/atoms/pinned-publications-atom";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useCurrentNotebook from "utilities/hooks/domain/notebooks/use-current-notebook";
import { useLocalization } from "utilities/hooks/use-localization";
import UserPublicationPinService from "utilities/services/user-publication-pins/user-publication-pin-service";
import { ToastManager } from "utilities/toast/toast-manager";

/**
 * Utility hook to load current publication pins
 */
export default function usePublicationPins() {
    const [pins, setPins] = useAtom(PinnedPublicationsAtoms);
    const [loading, setLoading] = useAtom(notebookPinsLoadingAtom);
    const { currentNotebook } = useCurrentNotebook();
    const { t } = useLocalization();
    const { globalState } = useGlobalState();

    const currentUserId = useMemo(
        () => globalState.currentIdentity?.userId(),
        [globalState]
    );

    const currentNotebookId = useMemo(
        () => currentNotebook?.id,
        [currentNotebook?.id]
    );

    const { list: listPublicationPinsApi } =
        UserPublicationPinService.useList();

    const loadPins = useCallback(
        (isInitialLoad: boolean = true) => {
            if (
                currentUserId == null ||
                !currentNotebookId ||
                currentUserId <= 0
            ) {
                return;
            }

            Do.try(async () => {
                if (isInitialLoad) {
                    setLoading(true);
                }
                const result = await listPublicationPinsApi({
                    userId: currentUserId,
                    notebookId: currentNotebookId,
                });
                setPins(result.resultObjects);
            })
                .catch(() =>
                    ToastManager.error(
                        t("errors-actionResource", {
                            action: t("loading"),
                            resource: t("favorite_plural"),
                        })
                    )
                )
                .finally(() => setLoading(false));
        },
        [
            currentNotebookId,
            currentUserId,
            listPublicationPinsApi,
            setLoading,
            setPins,
            t,
        ]
    );

    useEffect(() => loadPins(), [loadPins]);

    return {
        pins,
        setPins,
        loading,
        refresh: () => loadPins(false),
    };
}
